import React, { useState, useContext, Fragment, useEffect } from 'react';
import { Button, Col, Row, Form, Table, Modal, Badge, Card, Checkbox } from "react-bootstrap";
import Layout from "./layout/Layout";
import { CartContext } from '../../../context/CartContext';
import SucessSnackBar from '../../../components/SnackBar';
import ErrorSnackBar from '../../../components/SnackBar';
import api from '../../../helper/apiAdmin';
import { getServerURL } from '../../../helper/envConfig';
import './assets/css/admin.css';

const EpisodeManagement = () => {
  const { setMainLoder } = useContext(CartContext);
  
  // State management
  const [episodes, setEpisodes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [showAddEpisodeModal, setShowAddEpisodeModal] = useState(false);
  const [showAddSeasonModal, setShowAddSeasonModal] = useState(false);
  const [showEditSeriesModal, setShowEditSeriesModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showEditEpisodeModal, setShowEditEpisodeModal] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [Mymessage, setMyMessage] = useState("");
  const [sucessSnackBarOpen, setSucessSnackBarOpen] = useState(false);
  const [warningSnackBarOpen, setWarningSnackBarOpen] = useState(false);
  
  // Product list API state
  const [productList, setProductList] = useState([]);
  const [showProductListModal, setShowProductListModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const serverURL = getServerURL();

  // Form states
  const [episodeForm, setEpisodeForm] = useState({
    title: '',
    description: '',
    duration: '',
    season: '',
    episodeNumber: '',
    videoFile: null,
    thumbnail: null,
    products: []
  });

  const [editEpisodeForm, setEditEpisodeForm] = useState({
    title: '',
    description: '',
    duration: '',
    season: '',
    episodeNumber: '',
    videoFile: null,
    thumbnail: null,
    products: []
  });

  const [seasonForm, setSeasonForm] = useState({
    name: '',
    description: '',
    year: new Date().getFullYear(),
    genre: ''
  });

  const [editSeriesForm, setEditSeriesForm] = useState({
    name: '',
    description: '',
    year: new Date().getFullYear(),
    genre: '',
    cover_image: null
  });

  const [productForm, setProductForm] = useState({
    name: '',
    price: '',
    image: null,
    link: ''
  });

  // Fetch series and episodes from API
  const fetchSeries = async () => {
    try {
      setMainLoder(true);
      console.log('Fetching series from API...');
      const response = await api.getWithToken(`${serverURL}api/admin/series?page=1&limit=100`);
      
      console.log('Series fetch response:', response.data);
      
      if (response.data && response.data.success) {
        // Transform series data to match the expected format
        const transformedSeries = response.data.data.map(series => ({
          id: series._id,
          name: series.title,
          description: series.description,
          year: series.year,
          episodeCount: series.total_episodes || 0,
          genre: series.genre,
          cover_image: series.cover_image
        }));
        setSeasons(transformedSeries);
        console.log('Series loaded:', transformedSeries.length);
      } else {
        setMyMessage(response.data?.message || 'Error fetching series');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching series:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || 'Error fetching series';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  const fetchEpisodes = async (seriesId) => {
    try {
      setMainLoder(true);
      console.log('Fetching episodes for series ID:', seriesId);
      
      // Make sure seriesId is a valid ObjectId string
      if (!seriesId || typeof seriesId !== 'string' || seriesId.length !== 24) {
        console.error('Invalid series ID:', seriesId);
        setMyMessage('Invalid series ID');
        setWarningSnackBarOpen(true);
        return;
      }
      
      const response = await api.getWithToken(`${serverURL}api/admin/episodes?page=1&limit=100&series_id=${seriesId}`);
      
      console.log('Episodes fetch response:', response.data);
      
      if (response.data && response.data.success) {
        // Transform episode data to match the expected format
        const transformedEpisodes = response.data.data.map(episode => ({
          id: episode._id,
          title: episode.title,
          description: episode.description || 'No description',
          duration: episode.duration_formatted || 'N/A',
          season: `Season ${episode.season_number}`,
          episodeNumber: episode.episode_number,
          thumbnail: episode.thumbnail_url || 'https://via.placeholder.com/150x100',
          video_url: episode.video_url || '',
          video_file_name: episode.video_file_name || '',
          thumbnail_file_name: episode.thumbnail_file_name || '',
          categories: episode.categories || [],
          tags: episode.tags || [],
          is_premium: episode.is_premium || false,
          price: episode.price || 0,
          is_free: episode.episode_number <= 3, // CRITICAL FIX: Episodes 1-3 are free
          view_count: episode.view_count || 0,
          like_count: episode.like_count || 0,
          share_count: episode.share_count || 0,
          rating: episode.rating || 0,
          rating_count: episode.rating_count || 0,
          series_id: episode.series_id,
          season_number: episode.season_number,
          episode_number: episode.episode_number,
          created_by: episode.created_by,
          created_at: episode.created_at,
          updated_at: episode.updated_at,
          products: episode.products || []
        }));
        setEpisodes(transformedEpisodes);
        console.log('Episodes loaded:', transformedEpisodes.length);
        console.log('Transformed episodes:', transformedEpisodes);
      } else {
        setMyMessage(response.data?.message || 'Error fetching episodes');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || 'Error fetching episodes';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Initialize with API data
  React.useEffect(() => {
    fetchSeries();
  }, []);

  // Fetch episodes when series is selected
  React.useEffect(() => {
    if (selectedSeason) {
      fetchEpisodes(selectedSeason.id);
    }
  }, [selectedSeason]);

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts(1, "");
  }, []);

  // Fetch products from API
  const fetchProducts = async (page = 1, search = "") => {
    try {
      setMainLoder(true);
      const response = await api.postWithToken(`${serverURL}api/product-list-admin`, {
        page: page,
        limit: 10,
        search: search
      });
      
      if (response.data && response.data.data) {
        setProductList(response.data);
        setProductTotalPages(response.data.totalPages || 1);
      } else {
        setProductList({ data: [] });
        setProductTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setMyMessage('Error fetching products');
      setWarningSnackBarOpen(true);
      setProductList({ data: [] });
      setProductTotalPages(1);
    } finally {
      setMainLoder(false);
    }
  };

  // Handle form changes
  const handleEpisodeFormChange = (e) => {
    const { name, value } = e.target;
    setEpisodeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditEpisodeFormChange = (e) => {
    const { name, value } = e.target;
    setEditEpisodeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSeasonFormChange = (e) => {
    const { name, value } = e.target;
    setSeasonForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSeriesFormChange = (e) => {
    const { name, value } = e.target;
    setEditSeriesForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductFormChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Clear selected season
  const clearSelectedSeason = () => {
    setSelectedSeason(null);
    setEpisodes([]);
  };

  // Handle season selection
  const handleSeasonSelection = (season) => {
    console.log('Selecting season:', season);
    console.log('Season ID:', season.id);
    console.log('Season Name:', season.name);
    setSelectedSeason(season);
    // Automatically fetch episodes for the selected series
    fetchEpisodes(season.id);
  };

  // Handle file uploads
  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'video') {
        setEpisodeForm(prev => ({
          ...prev,
          videoFile: file
        }));
      } else if (type === 'thumbnail') {
        setEpisodeForm(prev => ({
          ...prev,
          thumbnail: file
        }));
      } else if (type === 'product') {
        setProductForm(prev => ({
          ...prev,
          image: file
        }));
      }
    }
  };

  // Handle edit episode file uploads
  const handleEditEpisodeFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'video') {
        setEditEpisodeForm(prev => ({
          ...prev,
          videoFile: file
        }));
      } else if (type === 'thumbnail') {
        setEditEpisodeForm(prev => ({
          ...prev,
          thumbnail: file
        }));
      }
    }
  };

  // Add new season (Series)
  const handleAddSeason = async () => {
    try {
      // Validate required fields
      if (seasonForm.name.trim() === '') {
        setMyMessage('Series name is required');
        setWarningSnackBarOpen(true);
        return;
      }

      setMainLoder(true);
      const formData = new FormData();
      
      // Add form fields to FormData
      formData.append('title', seasonForm.name);
      formData.append('description', seasonForm.description);
      formData.append('year', seasonForm.year);
      formData.append('genre', seasonForm.genre);
      formData.append('language', 'English');
      formData.append('age_rating', 'All Ages');
      formData.append('tags', '');

      console.log('Creating series with data:', seasonForm);
      console.log('Sending request to:', `${serverURL}api/admin/series`);
      
      // Debug FormData contents
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      const response = await api.postWithToken(`${serverURL}api/admin/series`, formData);
      
      console.log('Series creation response:', response);
      
      if (response.data && response.data.success) {
        setMyMessage('Series created successfully');
        setSucessSnackBarOpen(true);
        setSeasonForm({ name: '', description: '', year: new Date().getFullYear(), genre: '' });
        setShowAddSeasonModal(false);
        // Refresh the series list
        fetchSeries();
      } else {
        setMyMessage(response.data?.message || 'Error creating series');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error creating series:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 'Error creating series';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Open edit series modal
  const openEditSeriesModal = (series) => {
    setEditSeriesForm({
      name: series.name,
      description: series.description,
      year: series.year,
      genre: series.genre,
      cover_image: null
    });
    setSelectedSeason(series);
    setShowEditSeriesModal(true);
  };

  // Update series
  const handleUpdateSeries = async () => {
    try {
      // Validate required fields
      if (editSeriesForm.name.trim() === '') {
        setMyMessage('Series name is required');
        setWarningSnackBarOpen(true);
        return;
      }

      setMainLoder(true);
      const formData = new FormData();
      
      // Add form fields to FormData
      formData.append('title', editSeriesForm.name);
      formData.append('description', editSeriesForm.description);
      formData.append('year', editSeriesForm.year);
      formData.append('genre', editSeriesForm.genre);
      formData.append('language', 'English');
      formData.append('age_rating', 'All Ages');
      formData.append('tags', '');

      // Add cover image if provided
      if (editSeriesForm.cover_image) {
        formData.append('cover_image', editSeriesForm.cover_image);
      }
      
      const response = await api.putWithToken(`${serverURL}api/admin/series/${selectedSeason.id}`, formData);
      
      if (response.data && response.data.success) {
        setMyMessage('Series updated successfully');
        setSucessSnackBarOpen(true);
        setShowEditSeriesModal(false);
        // Refresh the series list
        fetchSeries();
        // Update selected season if it's the same one
        if (selectedSeason && selectedSeason.id === response.data.data._id) {
          setSelectedSeason({
            ...selectedSeason,
            name: editSeriesForm.name,
            description: editSeriesForm.description,
            year: editSeriesForm.year,
            genre: editSeriesForm.genre,
            cover_image: response.data.data.cover_image || selectedSeason.cover_image
          });
        }
      } else {
        setMyMessage(response.data?.message || 'Error updating series');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error updating series:', error);
      const errorMessage = error.response?.data?.message || 'Error updating series';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Add new episode
  const handleAddEpisode = async () => {
    try {
      // Validate required fields
      if (episodeForm.title.trim() === '') {
        setMyMessage('Episode title is required');
        setWarningSnackBarOpen(true);
        return;
      }
      
      if (!selectedSeason || !selectedSeason.id) {
        console.log('No series selected. selectedSeason:', selectedSeason);
        setMyMessage('Please select a series first');
        setWarningSnackBarOpen(true);
        return;
      }
      
      console.log('Selected season for episode creation:', selectedSeason);

      setMainLoder(true);
      const formData = new FormData();
      
      // Add form fields to FormData
      formData.append('series_id', selectedSeason.id);
      formData.append('season_number', '1'); // Default to season 1
      formData.append('episode_number', episodeForm.episodeNumber || '1');
      formData.append('title', episodeForm.title);
      formData.append('description', episodeForm.description);
      formData.append('duration', episodeForm.duration || '180');
      formData.append('categories', '');
      formData.append('tags', '');
      formData.append('is_premium', 'false');
      formData.append('price', '0');
      
      // Add products if they exist
      if (episodeForm.products && episodeForm.products.length > 0) {
        const productsArray = episodeForm.products.map(product => ({
          product_id: product.id,
          imageUrl: product.image,
          name: product.name
        }));
        formData.append('products', JSON.stringify(productsArray));
        console.log('Adding products to episode:', productsArray);
      }

      // Add files if they exist
      if (episodeForm.videoFile) {
        formData.append('video_file', episodeForm.videoFile);
      }
      if (episodeForm.thumbnail) {
        formData.append('thumbnail', episodeForm.thumbnail);
      }

      console.log('Creating episode with data:', episodeForm);
      console.log('Selected season:', selectedSeason);
      console.log('Sending request to:', `${serverURL}api/admin/episodes`);
      
      // Debug FormData contents
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
      }
      
      const response = await api.postWithToken(`${serverURL}api/admin/episodes`, formData);
      
      console.log('Episode creation response:', response);
      
      if (response.data && response.data.success) {
        setMyMessage('Episode added successfully');
        setSucessSnackBarOpen(true);
        setEpisodeForm({
          title: '',
          description: '',
          duration: '',
          season: '',
          episodeNumber: '',
          videoFile: null,
          thumbnail: null,
          products: []
        });
        setShowAddEpisodeModal(false);
        // Refresh the episodes list
        if (selectedSeason) {
          fetchEpisodes(selectedSeason.id);
        }
      } else {
        setMyMessage(response.data?.message || 'Error creating episode');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error creating episode:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 'Error creating episode';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Update existing episode
  const handleUpdateEpisode = async () => {
    if (editEpisodeForm.title.trim() === '' || editEpisodeForm.season === '') {
      setMyMessage('Title and season are required');
      setWarningSnackBarOpen(true);
      return;
    }

    try {
      setMainLoder(true);
      const formData = new FormData();
      
      // Add form fields to FormData
      formData.append('title', editEpisodeForm.title);
      formData.append('description', editEpisodeForm.description);
      formData.append('duration', editEpisodeForm.duration || '180');
      formData.append('season_number', '1'); // Default to season 1
      formData.append('episode_number', editEpisodeForm.episodeNumber || '1');
      formData.append('categories', '');
      formData.append('tags', '');
      formData.append('is_premium', 'false');
      formData.append('price', '0');
      
      // Add products if they exist
      if (editEpisodeForm.products && editEpisodeForm.products.length > 0) {
        const productsArray = editEpisodeForm.products.map(product => ({
          product_id: product.id,
          imageUrl: product.image,
          name: product.name
        }));
        formData.append('products', JSON.stringify(productsArray));
        console.log('Updating episode with products:', productsArray);
      }

      // Add files if they exist
      if (editEpisodeForm.videoFile) {
        formData.append('video_file', editEpisodeForm.videoFile);
      }
      if (editEpisodeForm.thumbnail) {
        formData.append('thumbnail', editEpisodeForm.thumbnail);
      }

      console.log('Updating episode:', selectedEpisode.id);
      console.log('Sending request to:', `${serverURL}api/admin/episodes/${selectedEpisode.id}`);
      
      // Debug FormData contents
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
      }
      
      const response = await api.putWithToken(`${serverURL}api/admin/episodes/${selectedEpisode.id}`, formData);
      
      console.log('Episode update response:', response);
      
      if (response.data && response.data.success) {
        setMyMessage('Episode updated successfully');
        setSucessSnackBarOpen(true);
        
        // Update local state with API response data
        setEpisodes(prev => prev.map(ep => 
          ep.id === selectedEpisode.id 
            ? { 
                ...ep, 
                title: response.data.data.title,
                description: response.data.data.description,
                duration: response.data.data.duration,
                duration_formatted: response.data.data.duration_formatted,
                thumbnail_url: response.data.data.thumbnail_url,
                video_url: response.data.data.video_url,
                products: response.data.data.products || ep.products
              }
            : ep
        ));
        
        setEditEpisodeForm({
          title: '',
          description: '',
          duration: '',
          season: '',
          episodeNumber: '',
          videoFile: null,
          thumbnail: null,
          products: []
        });
        setShowEditEpisodeModal(false);
        setSelectedEpisode(null);
        
        // Refresh episodes list
        if (selectedSeason) {
          fetchEpisodes(selectedSeason.id);
        }
      } else {
        setMyMessage(response.data?.message || 'Error updating episode');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error updating episode:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 'Error updating episode';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Open Add Episode Modal with pre-selected season
  const openAddEpisodeModal = () => {
    if (selectedSeason) {
      setEpisodeForm(prev => ({
        ...prev,
        season: selectedSeason.name
      }));
    }
    setShowAddEpisodeModal(true);
  };

  // Open Edit Episode Modal
  const openEditEpisodeModal = (episode) => {
    setEditEpisodeForm({
      title: episode.title,
      description: episode.description,
      duration: episode.duration,
      season: episode.season,
      episodeNumber: episode.episodeNumber,
      videoFile: null, // Reset file inputs
      thumbnail: null, // Reset file inputs
      products: episode.products || []
    });
    setSelectedEpisode(episode);
    setShowEditEpisodeModal(true);
  };

  // Open Product List Modal
  const openProductListModal = (episode) => {
    console.log('Opening product list modal for episode:', episode);
    console.log('Episode products:', episode.products);
    setSelectedEpisode(episode);
    setSelectedProducts(episode.products || []);
    setProductSearch("");
    setProductPage(1);
    fetchProducts(1, "");
    setShowProductListModal(true);
  };

  // Add product to episode
  const handleAddProduct = () => {
    if (productForm.name.trim() === '' || productForm.price.trim() === '') {
      setMyMessage('Product name and price are required');
      setWarningSnackBarOpen(true);
      return;
    }

    const newProduct = {
      id: Date.now(),
      ...productForm
    };

    if (selectedEpisode) {
      setEpisodes(prev => prev.map(ep => 
        ep.id === selectedEpisode.id 
          ? { ...ep, products: [...ep.products, newProduct] }
          : ep
      ));
    }

    setProductForm({ name: '', price: '', image: null, link: '' });
    setShowProductModal(false);
    setMyMessage('Product added to episode successfully');
    setSucessSnackBarOpen(true);
  };

  // Handle product selection from product list
  const handleProductSelection = (product) => {
    console.log('Product selected:', product);
    const isSelected = selectedProducts.find(p => p.id === product._id);
    console.log('Is product already selected:', !!isSelected);
    
    if (isSelected) {
      console.log('Removing product from selection');
      setSelectedProducts(prev => prev.filter(p => p.id !== product._id));
    } else {
      // Simplified product object - just ID, image, and name
      const productToAdd = {
        id: product._id,
        name: product.name,
        image: `https://ds90bjq9h592y.cloudfront.net/${product._id}/${product.product_images?.[0]?.file_name}`
      };
      console.log('Adding product to selection:', productToAdd);
      setSelectedProducts(prev => {
        const newSelection = [...prev, productToAdd];
        console.log('Updated selectedProducts array:', newSelection);
        return newSelection;
      });
    }
  };

  // Save selected products to episode
  const saveSelectedProducts = async () => {
    if (selectedEpisode) {
      try {
        setMainLoder(true);
        
        // Create FormData for the update
        const formData = new FormData();
        
        // Add products to FormData in the new simplified format
        console.log('=== FRONTEND DEBUG ===');
        console.log('Selected products array:', selectedProducts);
        console.log('Selected products length:', selectedProducts ? selectedProducts.length : 'undefined');
        console.log('Selected products type:', typeof selectedProducts);
        console.log('Selected products is array:', Array.isArray(selectedProducts));
        
        if (selectedProducts && selectedProducts.length > 0) {
          // Convert to the new format: [{product_id, imageUrl, name}]
          const productsArray = selectedProducts.map(product => ({
            product_id: product.id,
            imageUrl: product.image,
            name: product.name
          }));
          
          formData.append('products', JSON.stringify(productsArray));
          formData.append('mode', 'add');
          console.log('Adding products to episode:', productsArray);
          console.log('Products JSON string:', JSON.stringify(productsArray));
        } else {
          console.log('❌ No products selected or selectedProducts is empty');
          console.log('This is why you get empty products array!');
          formData.append('products', JSON.stringify([]));
          formData.append('mode', 'add');
        }
        
        const response = await api.putWithToken(`${serverURL}api/admin/episodes/${selectedEpisode.id}/products`, formData);
        
        if (response.data && response.data.success) {
          // Update local state
          setEpisodes(prev => prev.map(ep => 
            ep.id === selectedEpisode.id 
              ? { ...ep, products: selectedProducts }
              : ep
          ));
          
          setMyMessage(`${selectedProducts.length} products added to episode successfully`);
          setSucessSnackBarOpen(true);
          setShowProductListModal(false);
          setSelectedProducts([]);
          setSelectedEpisode(null);
          
          // Refresh episodes list
          if (selectedSeason) {
            fetchEpisodes(selectedSeason.id);
          }
        } else {
          setMyMessage(response.data?.message || 'Error adding products to episode');
          setWarningSnackBarOpen(true);
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Error adding products to episode';
        setMyMessage(errorMessage);
        setWarningSnackBarOpen(true);
      } finally {
        setMainLoder(false);
      }
    }
  };

  // Quick add product to episode (simplified)
  const quickAddProductToEpisode = (episodeId, product) => {
    const simplifiedProduct = {
      id: product._id,
      name: product.name,
      image: `https://ds90bjq9h592y.cloudfront.net/${product._id}/${product.product_images?.[0]?.file_name}`
    };

    setEpisodes(prev => prev.map(ep => 
      ep.id === episodeId 
        ? { ...ep, products: [...ep.products, simplifiedProduct] }
        : ep
    ));
    
    setMyMessage(`Product "${product.name}" added to episode successfully`);
    setSucessSnackBarOpen(true);
  };

  // Remove all products from episode
  const removeAllProductsFromEpisode = async (episodeId) => {
    try {
      setMainLoder(true);
      
      // Find the episode to get current products
      const episode = episodes.find(ep => ep.id === episodeId);
      
      if (!episode || !episode.products || episode.products.length === 0) {
        setMyMessage('No products to remove');
        setWarningSnackBarOpen(true);
        setMainLoder(false);
        return;
      }
      
      // Get all product IDs to remove
      const productIdsToRemove = episode.products.map(product => product.product_id).filter(id => id);
      
      if (productIdsToRemove.length === 0) {
        setMyMessage('No valid product IDs found to remove');
        setWarningSnackBarOpen(true);
        setMainLoder(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('products', JSON.stringify(productIdsToRemove));
      formData.append('mode', 'remove');
      
      const response = await api.putWithToken(`${serverURL}api/admin/episodes/${episodeId}/products`, formData);
      
      if (response.data && response.data.success) {
        setMyMessage('All products removed from episode successfully');
        setSucessSnackBarOpen(true);
        
        // Refresh episodes list
        if (selectedSeason) {
          fetchEpisodes(selectedSeason.id);
        }
      } else {
        setMyMessage(response.data?.message || 'Error removing all products');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error removing all products:', error);
      setMyMessage('Error removing all products: ' + error.message);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Test function to manually add a product to an episode
  const testAddProductToEpisode = async (episodeId) => {
    try {
      setMainLoder(true);
      
      // Create a test product ID using the same ID from your previous test
      const testProductId = "68abd3a031dfbc98f3291eaf"; // Real product ID from your test
      
      // Create test product in the correct format
      const testProduct = {
        product_id: testProductId,
        imageUrl: "https://ds90bjq9h592y.cloudfront.net/68abd3a031dfbc98f3291eaf/S36cc564b2d624760ada76d70bb86458ao.jpg",
        name: "Spring Summer Women's Dress Solid Color Sexy Sleeveless Chest Wrapped Long Beach Dress"
      };
      
      const formData = new FormData();
      formData.append('products', JSON.stringify([testProduct]));
      formData.append('mode', 'add');
      
      console.log('Testing product addition to episode:', episodeId);
      console.log('Test product:', testProduct);
      
      const response = await api.putWithToken(`${serverURL}api/admin/episodes/${episodeId}/products`, formData);
      
      console.log('Test response:', response);
      
      if (response.data && response.data.success) {
        setMyMessage('Test product added successfully');
        setSucessSnackBarOpen(true);
        
        // Refresh episodes list
        if (selectedSeason) {
          fetchEpisodes(selectedSeason.id);
        }
      } else {
        setMyMessage(response.data?.message || 'Error adding test product');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error in test:', error);
      setMyMessage('Error in test: ' + error.message);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Delete episode
  const handleDeleteEpisode = async (episodeId) => {
    try {
      // Show confirmation dialog
      const episodeToDelete = episodes.find(ep => ep.id === episodeId);
      if (!episodeToDelete) {
        setMyMessage('Episode not found');
        setWarningSnackBarOpen(true);
        return;
      }

      const confirmDelete = window.confirm(
        `Are you sure you want to delete "${episodeToDelete.title}"? This action cannot be undone.`
      );
      
      if (!confirmDelete) {
        return;
      }

      setMainLoder(true);
      
      // Make API call to delete episode
      const response = await api.deleteWithToken(`${serverURL}api/admin/episodes/${episodeId}`);
      
      if (response.data && response.data.success) {
        // Update local state only after successful API call
        setEpisodes(prev => prev.filter(ep => ep.id !== episodeId));
        
        // Update episode count for the season
        setSeasons(prev => prev.map(season => 
          season.id === episodeToDelete.series_id?.id || season.name === episodeToDelete.series_id?.title
            ? { ...season, episodeCount: Math.max(0, season.episodeCount - 1) }
            : season
        ));
        
        setMyMessage('Episode deleted successfully');
        setSucessSnackBarOpen(true);
      } else {
        setMyMessage(response.data?.message || 'Error deleting episode');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error deleting episode:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting episode';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Delete entire series (season)
  const handleDeleteSeries = async (seriesId) => {
    try {
      // Show confirmation dialog
      const seriesToDelete = seasons.find(season => season.id === seriesId);
      if (!seriesToDelete) {
        setMyMessage('Series not found');
        setWarningSnackBarOpen(true);
        return;
      }

      const confirmDelete = window.confirm(
        `Are you sure you want to delete the entire series "${seriesToDelete.name}" and ALL its episodes? This action cannot be undone.`
      );
      
      if (!confirmDelete) {
        return;
      }

      setMainLoder(true);
      
      // Make API call to delete series
      const response = await api.deleteWithToken(`${serverURL}api/admin/series/${seriesId}`);
      
      if (response.data && response.data.success) {
        // Update local state only after successful API call
        setSeasons(prev => prev.filter(season => season.id !== seriesId));
        
        // Clear episodes if the deleted series was selected
        if (selectedSeason && selectedSeason.id === seriesId) {
          setSelectedSeason(null);
          setEpisodes([]);
        } else {
          // Remove episodes that belonged to the deleted series
          setEpisodes(prev => prev.filter(ep => ep.series_id !== seriesId));
        }
        
        setMyMessage('Series and all episodes deleted successfully');
        setSucessSnackBarOpen(true);
      } else {
        setMyMessage(response.data?.message || 'Error deleting series');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error deleting series:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting series';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  // Delete product from episode
  const handleDeleteProduct = async (episodeId, productId) => {
    try {
      // Validate productId
      if (!productId || productId.trim() === '') {
        setMyMessage('Invalid product ID');
        setWarningSnackBarOpen(true);
        return;
      }
      
      // Find the episode to get current products
      const episode = episodes.find(ep => ep.id === episodeId);
      
      if (!episode) {
        setMyMessage('Episode not found');
        setWarningSnackBarOpen(true);
        return;
      }
      
      setMainLoder(true);
      
      // Create FormData for the update
      const formData = new FormData();
      formData.append('products', JSON.stringify([productId]));
      formData.append('mode', 'remove');
      
      const response = await api.putWithToken(`${serverURL}api/admin/episodes/${episodeId}/products`, formData);
      
      if (response.data && response.data.success) {
        // Update local state - remove the product from the episode's products array
        setEpisodes(prev => prev.map(ep => 
          ep.id === episodeId 
            ? { ...ep, products: ep.products.filter(p => p.product_id !== productId) }
            : ep
        ));
        
        setMyMessage('Product removed from episode successfully');
        setSucessSnackBarOpen(true);
        
        // Refresh episodes list
        if (selectedSeason) {
          fetchEpisodes(selectedSeason.id);
        }
      } else {
        setMyMessage(response.data?.message || 'Error removing product from episode');
        setWarningSnackBarOpen(true);
      }
    } catch (error) {
      console.error('Error removing product from episode:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 'Error removing product from episode';
      setMyMessage(errorMessage);
      setWarningSnackBarOpen(true);
    } finally {
      setMainLoder(false);
    }
  };

  return (
    <Fragment>
      <Layout>
        <div className="episode-management">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Episode Management</h2>
            <div>
              <Button 
                variant="outline-primary" 
                className="me-2"
                onClick={() => setShowAddSeasonModal(true)}
              >
                Add Series
              </Button>
                             <Button 
                 variant="primary"
                 onClick={openAddEpisodeModal}
                 disabled={!selectedSeason}
               >
                 Add Episode {selectedSeason ? `to ${selectedSeason.name}` : '(Select Series First)'}
               </Button>
            </div>
          </div>

                    {/* Seasons Overview */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Series Overview</h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th style={{ width: '200px' }}>Series Name</th>
                          <th style={{ width: '300px', maxWidth: '300px' }}>Description</th>
                          <th style={{ width: '80px' }}>Year</th>
                          <th style={{ width: '100px' }}>Genre</th>
                          <th style={{ width: '100px' }}>Episodes</th>
                          <th style={{ width: '120px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seasons.map(season => (
                          <tr 
                            key={season.id} 
                            className={`season-row ${selectedSeason?.id === season.id ? 'table-active' : ''}`}
                            onClick={() => handleSeasonSelection(season)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="d-flex align-items-center">
                                {season.cover_image && (
                                  <img 
                                    src={season.cover_image} 
                                    alt={season.name}
                                    style={{ width: '40px', height: '40px', objectFit: 'cover', marginRight: '10px' }}
                                    className="rounded"
                                  />
                                )}
                                <strong>{season.name}</strong>
                              </div>
                            </td>
                            <td style={{ maxWidth: '300px', overflow: 'hidden' }}>
                              <div className="text-truncate" title={season.description}>
                                {season.description}
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{season.year}</span>
                            </td>
                            <td>
                              {season.genre && (
                                <span className="badge bg-success">{season.genre}</span>
                              )}
                            </td>
                            <td>
                              <span className="badge bg-info">{season.episodeCount} Episodes</span>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline-warning"
                                  onClick={() => openEditSeriesModal(season)}
                                >
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row selection
                                    handleDeleteSeries(season.id);
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  
                  {/* Selected Series Details */}
                  {selectedSeason && (
                    <div className="mt-4 p-3 bg-light rounded">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Selected Series: {selectedSeason.name}</h6>
                        <Button 
                          size="sm" 
                          variant="outline-secondary"
                          onClick={clearSelectedSeason}
                        >
                          Clear Selection
                        </Button>
                      </div>
                      <Row>
                        <Col md={4}>
                          {selectedSeason.cover_image && (
                            <img 
                              src={selectedSeason.cover_image} 
                              alt={selectedSeason.name}
                              style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                              className="rounded mb-3"
                            />
                          )}
                        </Col>
                        <Col md={8}>
                          <p><strong>Description:</strong> {selectedSeason.description}</p>
                          <p><strong>Year:</strong> {selectedSeason.year}</p>
                          <p><strong>Genre:</strong> {selectedSeason.genre}</p>
                          <p><strong>Total Episodes:</strong> {selectedSeason.episodeCount}</p>
                          <p><strong>Episodes in this series:</strong></p>
                          <ul className="list-unstyled">
                            {episodes
                              .filter(ep => ep.season === selectedSeason.name)
                              .map(ep => (
                                <li key={ep.id} className="mb-1">
                                  <small className="text-muted">
                                    • {ep.title} ({ep.duration})
                                  </small>
                                </li>
                              ))
                            }
                          </ul>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Episodes Table */}
          <Row>
            <Col>
              <Card>
                <Card.Header className={selectedSeason ? 'season-filter-header' : ''}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      {selectedSeason ? `Episodes - ${selectedSeason.name}` : 'All Episodes'}
                    </h5>
                    {selectedSeason && (
                      <Button 
                        size="sm" 
                        variant="outline-secondary"
                        onClick={clearSelectedSeason}
                      >
                        Show All Episodes
                      </Button>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  {selectedSeason ? (
                    // Show episodes for selected season
                    <>
                      {episodes.length > 0 ? (
                        <div className="table-responsive">
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th style={{ width: '80px' }}>Thumbnail</th>
                                <th style={{ width: '200px', maxWidth: '200px' }}>Title</th>
                                <th style={{ width: '150px' }}>Series</th>
                                <th style={{ width: '80px' }}>Season</th>
                                <th style={{ width: '80px' }}>Episode #</th>
                                <th style={{ width: '80px' }}>Duration</th>
                                <th style={{ width: '100px' }}>Type</th>
                                <th style={{ width: '200px' }}>Products</th>
                                <th style={{ width: '120px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {episodes.map(episode => (
                                  <tr key={episode.id}>
                                    <td>
                                      <img 
                                        src={episode.thumbnail} 
                                        alt={episode.title}
                                        style={{ width: '80px', height: '50px', objectFit: 'cover' }}
                                        className="rounded"
                                      />
                                    </td>
                                    <td style={{ maxWidth: '200px', overflow: 'hidden' }}>
                                      <div className="text-truncate" title={episode.title}>
                                        <strong>{episode.title}</strong>
                                      </div>
                                      {episode.description && (
                                        <div className="text-truncate text-muted small" title={episode.description}>
                                          {episode.description}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div>
                                        <strong>{episode.series_id?.title || 'Unknown Series'}</strong>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="badge bg-secondary">Season {episode.season_number}</span>
                                    </td>
                                    <td>
                                      <span className="badge bg-primary">Episode {episode.episode_number}</span>
                                    </td>
                                    <td>{episode.duration}</td>
                                    <td>
                                      {episode.is_premium ? (
                                        <span className="badge bg-warning">Premium (${episode.price})</span>
                                      ) : (
                                        <span className="badge bg-success">Free</span>
                                      )}
                                    </td>
                                    <td style={{ maxWidth: '200px' }}>
                                      <div className="d-flex flex-wrap gap-1 mb-2">
                                        {episode.products && episode.products.length > 0 ? (
                                          episode.products.slice(0, 2).map(product => (
                                            <Badge 
                                              key={product._id || product.product_id} 
                                              bg="success" 
                                              className="d-flex align-items-center text-truncate"
                                              style={{ maxWidth: '150px' }}
                                            >
                                              {product.product_image && (
                                                <img 
                                                  src={product.product_image} 
                                                  alt={product.product_name}
                                                  style={{ width: '16px', height: '16px', marginRight: '4px' }}
                                                  className="rounded"
                                                />
                                              )}
                                              <span className="text-truncate" title={product.product_name}>
                                                {product.product_name}
                                              </span>
                                              <Button
                                                size="sm"
                                                variant="link"
                                                className="product-remove-btn p-0 ms-1"
                                                style={{ fontSize: '10px', color: 'white' }}
                                                onClick={() => handleDeleteProduct(episode.id, product.product_id)}
                                              >
                                                ×
                                              </Button>
                                            </Badge>
                                          ))
                                        ) : (
                                          <span className="text-muted small">No products</span>
                                        )}
                                        {episode.products && episode.products.length > 2 && (
                                          <Badge bg="secondary">+{episode.products.length - 2} more</Badge>
                                        )}
                                      </div>
                                      <div className="d-flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          onClick={() => openProductListModal(episode)}
                                          style={{ fontSize: '11px' }}
                                        >
                                          Add
                                        </Button>
                                        {episode.products && episode.products.length > 0 && (
                                          <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => removeAllProductsFromEpisode(episode.id)}
                                            style={{ fontSize: '11px' }}
                                          >
                                            Clear All
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                    <td style={{ minWidth: '120px' }}>
                                      <div className="d-flex gap-1">
                                        <Button 
                                          size="sm" 
                                          variant="outline-warning"
                                          onClick={() => openEditEpisodeModal(episode)}
                                          style={{ fontSize: '11px' }}
                                        >
                                          Edit
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline-danger"
                                          onClick={() => handleDeleteEpisode(episode.id)}
                                          style={{ fontSize: '11px' }}
                                        >
                                          Delete
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                                                <div className="text-center py-5 empty-state">
                          <div className="mb-3">
                            <i className="fas fa-video" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                          </div>
                          <h6 className="text-muted">No Episodes Found</h6>
                          <p className="text-muted mb-3">
                            {selectedSeason.name} doesn't have any episodes yet.
                          </p>
                          <Button 
                            variant="primary"
                            onClick={openAddEpisodeModal}
                          >
                            Add First Episode
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    // Show all episodes when no season is selected
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th style={{ width: '80px' }}>Thumbnail</th>
                            <th style={{ width: '200px', maxWidth: '200px' }}>Title</th>
                            <th style={{ width: '150px' }}>Series</th>
                            <th style={{ width: '80px' }}>Season</th>
                            <th style={{ width: '80px' }}>Episode #</th>
                            <th style={{ width: '80px' }}>Duration</th>
                            <th style={{ width: '100px' }}>Type</th>
                            <th style={{ width: '200px' }}>Products</th>
                            <th style={{ width: '120px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {episodes.map(episode => (
                            <tr key={episode.id}>
                              <td>
                                <img 
                                  src={episode.thumbnail} 
                                  alt={episode.title}
                                  style={{ width: '80px', height: '50px', objectFit: 'cover' }}
                                  className="rounded"
                                />
                              </td>
                              <td style={{ maxWidth: '200px', overflow: 'hidden' }}>
                                <div className="text-truncate" title={episode.title}>
                                  <strong>{episode.title}</strong>
                                </div>
                                {episode.description && (
                                  <div className="text-truncate text-muted small" title={episode.description}>
                                    {episode.description}
                                  </div>
                                )}
                              </td>
                              <td>
                                <div>
                                  <strong>{episode.series_id?.title || 'Unknown Series'}</strong>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-secondary">Season {episode.season_number}</span>
                              </td>
                              <td>
                                <span className="badge bg-primary">Episode {episode.episode_number}</span>
                              </td>
                              <td>{episode.duration}</td>
                              <td>
                                {episode.is_premium ? (
                                  <span className="badge bg-warning">Premium (${episode.price})</span>
                                ) : (
                                  <span className="badge bg-success">Free</span>
                                )}
                              </td>
                              <td style={{ maxWidth: '200px' }}>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  {episode.products && episode.products.length > 0 ? (
                                    episode.products.slice(0, 2).map(product => (
                                      <Badge 
                                        key={product._id || product.product_id} 
                                        bg="success" 
                                        className="d-flex align-items-center text-truncate"
                                        style={{ maxWidth: '150px' }}
                                      >
                                        {product.product_image && (
                                          <img 
                                            src={product.product_image} 
                                            alt={product.product_name}
                                            style={{ width: '16px', height: '16px', marginRight: '4px' }}
                                            className="rounded"
                                          />
                                        )}
                                        <span className="text-truncate" title={product.product_name}>
                                          {product.product_name}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="link"
                                          className="product-remove-btn p-0 ms-1"
                                          style={{ fontSize: '10px', color: 'white' }}
                                          onClick={() => handleDeleteProduct(episode.id, product.product_id)}
                                        >
                                          ×
                                        </Button>
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-muted small">No products</span>
                                  )}
                                  {episode.products && episode.products.length > 2 && (
                                    <Badge bg="secondary">+{episode.products.length - 2} more</Badge>
                                  )}
                                </div>
                                <div className="d-flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => openProductListModal(episode)}
                                    style={{ fontSize: '11px' }}
                                  >
                                    Add
                                  </Button>
                                  {episode.products && episode.products.length > 0 && (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => removeAllProductsFromEpisode(episode.id)}
                                      style={{ fontSize: '11px' }}
                                    >
                                      Clear All
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td style={{ minWidth: '120px' }}>
                                <div className="d-flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="outline-warning"
                                    onClick={() => openEditEpisodeModal(episode)}
                                    style={{ fontSize: '11px' }}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline-danger"
                                    onClick={() => handleDeleteEpisode(episode.id)}
                                    style={{ fontSize: '11px' }}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Add Series Modal */}
        <Modal show={showAddSeasonModal} onHide={() => setShowAddSeasonModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Series</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Series Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={seasonForm.name}
                  onChange={handleSeasonFormChange}
                  placeholder="e.g., My Awesome Series"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={seasonForm.description}
                  onChange={handleSeasonFormChange}
                  placeholder="Series description..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Year</Form.Label>
                <Form.Control
                  type="number"
                  name="year"
                  value={seasonForm.year}
                  onChange={handleSeasonFormChange}
                  min="2000"
                  max="2030"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Genre</Form.Label>
                <Form.Select
                  name="genre"
                  value={seasonForm.genre}
                  onChange={handleSeasonFormChange}
                >
                  <option value="">Select Genre</option>
                  <option value="Drama">Drama</option>
                  <option value="Comedy">Comedy</option>
                  <option value="Action">Action</option>
                  <option value="Thriller">Thriller</option>
                  <option value="Romance">Romance</option>
                  <option value="Horror">Horror</option>
                  <option value="Sci-Fi">Sci-Fi</option>
                  <option value="Fantasy">Fantasy</option>
                  <option value="Documentary">Documentary</option>
                  <option value="Reality">Reality</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddSeasonModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddSeason}>
              Add Series
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Series Modal */}
        <Modal show={showEditSeriesModal} onHide={() => setShowEditSeriesModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Series</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Series Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={editSeriesForm.name}
                  onChange={handleEditSeriesFormChange}
                  placeholder="e.g., My Awesome Series"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={editSeriesForm.description}
                  onChange={handleEditSeriesFormChange}
                  placeholder="Series description..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Year</Form.Label>
                <Form.Control
                  type="number"
                  name="year"
                  value={editSeriesForm.year}
                  onChange={handleEditSeriesFormChange}
                  min="2000"
                  max="2030"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Genre</Form.Label>
                <Form.Select
                  name="genre"
                  value={editSeriesForm.genre}
                  onChange={handleEditSeriesFormChange}
                >
                  <option value="">Select Genre</option>
                  <option value="Drama">Drama</option>
                  <option value="Comedy">Comedy</option>
                  <option value="Action">Action</option>
                  <option value="Thriller">Thriller</option>
                  <option value="Romance">Romance</option>
                  <option value="Horror">Horror</option>
                  <option value="Sci-Fi">Sci-Fi</option>
                  <option value="Fantasy">Fantasy</option>
                  <option value="Documentary">Documentary</option>
                  <option value="Reality">Reality</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Cover Image (Optional)</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setEditSeriesForm(prev => ({
                        ...prev,
                        cover_image: file
                      }));
                    }
                  }}
                />
                {selectedSeason && selectedSeason.cover_image && !editSeriesForm.cover_image && (
                  <div className="mt-2">
                    <small className="text-muted">Current cover image:</small>
                    <img 
                      src={selectedSeason.cover_image} 
                      alt="Current cover"
                      style={{ width: '100px', height: '60px', objectFit: 'cover' }}
                      className="rounded ms-2"
                    />
                  </div>
                )}
                {editSeriesForm.cover_image && editSeriesForm.cover_image instanceof File && (
                  <div className="mt-2">
                    <small className="text-muted">New cover image preview:</small>
                    <img 
                      src={URL.createObjectURL(editSeriesForm.cover_image)} 
                      alt="New cover preview"
                      style={{ width: '100px', height: '60px', objectFit: 'cover' }}
                      className="rounded ms-2"
                    />
                  </div>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditSeriesModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateSeries}>
              Update Series
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add Episode Modal */}
        <Modal show={showAddEpisodeModal} onHide={() => setShowAddEpisodeModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Add New Episode</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Episode Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={episodeForm.title}
                      onChange={handleEpisodeFormChange}
                      placeholder="e.g., Episode 1: The Beginning"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Episode Number</Form.Label>
                    <Form.Control
                      type="number"
                      name="episodeNumber"
                      value={episodeForm.episodeNumber}
                      onChange={handleEpisodeFormChange}
                      placeholder="1"
                      min="1"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Season</Form.Label>
                    <Form.Select
                      name="season"
                      value={episodeForm.season}
                      onChange={handleEpisodeFormChange}
                    >
                      <option value="">Select Season</option>
                      {seasons.map(season => (
                        <option key={season.id} value={season.name}>
                          {season.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      name="duration"
                      value={episodeForm.duration}
                      onChange={handleEpisodeFormChange}
                      placeholder="1-3"
                      min="1"
                      max="3"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={episodeForm.description}
                  onChange={handleEpisodeFormChange}
                  placeholder="Episode description..."
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Video File</Form.Label>
                    <Form.Control
                      type="file"
                      accept="video/*"
                      onChange={(e) => handleFileUpload(e, 'video')}
                    />
                    <Form.Text className="text-muted">
                      Maximum 3 minutes, MP4 format recommended
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Thumbnail</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'thumbnail')}
                    />
                    <Form.Text className="text-muted">
                      Recommended size: 150x100px
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddEpisodeModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddEpisode}>
              Add Episode
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Episode Modal */}
        <Modal show={showEditEpisodeModal} onHide={() => setShowEditEpisodeModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Episode</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Episode Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={editEpisodeForm.title}
                      onChange={handleEditEpisodeFormChange}
                      placeholder="e.g., Episode 1: The Beginning"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Episode Number</Form.Label>
                    <Form.Control
                      type="number"
                      name="episodeNumber"
                      value={editEpisodeForm.episodeNumber}
                      onChange={handleEditEpisodeFormChange}
                      placeholder="1"
                      min="1"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Season</Form.Label>
                    <Form.Select
                      name="season"
                      value={editEpisodeForm.season}
                      onChange={handleEditEpisodeFormChange}
                    >
                      <option value="">Select Season</option>
                      {seasons.map(season => (
                        <option key={season.id} value={season.name}>
                          {season.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      name="duration"
                      value={editEpisodeForm.duration}
                      onChange={handleEditEpisodeFormChange}
                      placeholder="1-3"
                      min="1"
                      max="3"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={editEpisodeForm.description}
                  onChange={handleEditEpisodeFormChange}
                  placeholder="Episode description..."
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Video File</Form.Label>
                    <Form.Control
                      type="file"
                      accept="video/*"
                      onChange={(e) => handleEditEpisodeFileUpload(e, 'video')}
                    />
                    <Form.Text className="text-muted">
                      Maximum 3 minutes, MP4 format recommended
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Thumbnail</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleEditEpisodeFileUpload(e, 'thumbnail')}
                    />
                    <Form.Text className="text-muted">
                      Recommended size: 150x100px
                    </Form.Text>
                    {selectedEpisode && selectedEpisode.thumbnail_url && !editEpisodeForm.thumbnail && (
                      <div className="mt-2">
                        <small className="text-muted">Current thumbnail:</small>
                        <img 
                          src={selectedEpisode.thumbnail_url} 
                          alt="Current thumbnail"
                          style={{ width: '100px', height: '60px', objectFit: 'cover' }}
                          className="rounded ms-2"
                        />
                      </div>
                    )}
                    {editEpisodeForm.thumbnail && editEpisodeForm.thumbnail instanceof File && (
                      <div className="mt-2">
                        <small className="text-muted">New thumbnail preview:</small>
                        <img 
                          src={URL.createObjectURL(editEpisodeForm.thumbnail)} 
                          alt="New thumbnail preview"
                          style={{ width: '100px', height: '60px', objectFit: 'cover' }}
                          className="rounded ms-2"
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditEpisodeModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateEpisode}>
              Update Episode
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Product List Modal */}
        <Modal 
          show={showProductListModal} 
          onHide={() => setShowProductListModal(false)} 
          size="xl" 
          className="product-list-modal"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Select Products for Episode</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3 search-section">
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Search products..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    style={{ width: '300px' }}
                  />
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => fetchProducts(1, productSearch)}
                  >
                    Search
                  </Button>
                </div>
                <div>
                  <span className="badge bg-primary">
                    {selectedProducts.length} products selected
                  </span>
                </div>
              </div>
              
              {/* Selected Products Display */}
              {selectedProducts.length > 0 && (
                <div className="mb-3 p-3 selected-products-section rounded">
                  <h6 className="mb-2">Selected Products:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedProducts.map(product => (
                      <Badge 
                        key={product.id} 
                        bg="success" 
                        className="d-flex align-items-center"
                      >
                        <img 
                          src={product.image} 
                          alt={product.name}
                          style={{ width: '20px', height: '20px', marginRight: '5px' }}
                          className="rounded"
                        />
                        {product.name}
                        <Button
                          size="sm"
                          variant="link"
                          className="product-remove-btn p-0 ms-1"
                          style={{ fontSize: '12px' }}
                          onClick={() => {
                            setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
                          }}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {productList && productList.data ? (
                 productList.data.length > 0 ? (
                   <div className="table-responsive">
                     <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th width="5%">Select</th>
                        <th width="15%">Image</th>
                        <th width="25%">Name</th>
                        <th width="15%">Category</th>
                        <th width="15%">Price</th>
                        <th width="15%">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productList.data.map((product, index) => {
                        const isSelected = selectedProducts.find(p => p.id === product._id);
                        return (
                          <tr key={product._id}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleProductSelection(product)}
                              />
                            </td>
                            <td>
                                                             <img 
                                 src={`https://ds90bjq9h592y.cloudfront.net/${product._id}/${product.product_images?.[0]?.file_name}`}
                                 alt={product.name}
                                 style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                 className="rounded"
                               />
                            </td>
                            <td>
                              <strong>{product.name}</strong>
                              <br />
                              <small className="text-muted">
                                {product.product_category_keys?.product_category_one?.name} 
                                {product.product_category_keys?.product_category_two?.name && 
                                  ` > ${product.product_category_keys.product_category_two.name}`
                                }
                              </small>
                            </td>
                            <td>
                              {product.product_category_keys?.product_category_one?.name || 'N/A'}
                            </td>
                            <td>
                              <span className="badge bg-success">
                                ${product.individual_price}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${product.is_active === 1 ? 'bg-success' : 'bg-secondary'}`}>
                                {product.is_active === 1 ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                                         </tbody>
                   </Table>
                 </div>
                 ) : (
                   <div className="text-center py-4">
                     <p className="text-muted">No products found</p>
                   </div>
                 )
               ) : (
                 <div className="text-center py-4">
                   <div className="spinner-border text-primary" role="status">
                     <span className="visually-hidden">Loading...</span>
                   </div>
                   <p className="text-muted mt-2">Loading products...</p>
                 </div>
               )}
              
              {/* Pagination */}
              {productTotalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <nav>
                    <ul className="pagination">
                                             <li className={`page-item ${productPage === 1 ? 'disabled' : ''}`}>
                         <Button 
                           className="page-link" 
                           variant="outline-primary"
                           onClick={() => {
                             const newPage = productPage - 1;
                             setProductPage(newPage);
                             fetchProducts(newPage, productSearch);
                           }}
                           disabled={productPage === 1}
                         >
                           ←
                         </Button>
                       </li>
                      {Array.from({ length: Math.min(5, productTotalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <li key={pageNum} className={`page-item ${pageNum === productPage ? 'active' : ''}`}>
                            <Button 
                              className="page-link" 
                              variant={pageNum === productPage ? 'primary' : 'outline-primary'}
                              onClick={() => {
                                setProductPage(pageNum);
                                fetchProducts(pageNum, productSearch);
                              }}
                            >
                              {pageNum}
                            </Button>
                          </li>
                        );
                      })}
                                             <li className={`page-item ${productPage === productTotalPages ? 'disabled' : ''}`}>
                         <Button 
                           className="page-link" 
                           variant="outline-primary"
                           onClick={() => {
                             const newPage = productPage + 1;
                             setProductPage(newPage);
                             fetchProducts(newPage, productSearch);
                           }}
                           disabled={productPage === productTotalPages}
                                                    >
                             →
                           </Button>
                       </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProductListModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={saveSelectedProducts}>
              Add {selectedProducts.length} Products to Episode
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add Product Modal */}
        <Modal show={showProductModal} onHide={() => setShowProductModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add Product to Episode</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Product Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={productForm.name}
                  onChange={handleProductFormChange}
                  placeholder="Product name"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Price</Form.Label>
                <Form.Control
                  type="text"
                  name="price"
                  value={productForm.price}
                  onChange={handleProductFormChange}
                  placeholder="e.g., $29.99"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Product Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'product')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Product Link (Optional)</Form.Label>
                <Form.Control
                  type="url"
                  name="link"
                  value={productForm.link}
                  onChange={handleProductFormChange}
                  placeholder="https://example.com/product"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProductModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddProduct}>
              Add Product
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Snackbar Notifications */}
        <SucessSnackBar
          open={sucessSnackBarOpen}
          setOpen={setSucessSnackBarOpen}
          text={Mymessage}
          type="success"
        />
        <ErrorSnackBar
          open={warningSnackBarOpen}
          setOpen={setWarningSnackBarOpen}
          text={Mymessage}
          type="error"
        />
      </Layout>
    </Fragment>
  );
};

export default EpisodeManagement; 